import { useBackendQuery as useQuery } from "@/hooks/useBackendQuery";
import { MaximizeIcon } from "lucide-react";

import { EntityFragment } from "@/__generated__/graphql";
import { UpdateNodeContextType } from "@/app/app/_/commands";
import { IObjectWithTypename } from "@/components/@types";
import { NodeLayout } from "@/components/organisms/Node/NodeLayout";
import { NodeTitleLayout } from "@/components/organisms/Node/NodeTitleLayout";
import { useNode, useNodeSetup } from "@/components/organisms/Node/Node.hooks";
import { StickyLayout } from "@/components/organisms/StickyLayout";
import { Queries } from "@/constants";
import {
  idToIdentifier,
  idToTypename,
  isSyntheticPanelId,
} from "@/objects/@utils";
import { State } from "@/state";

/**
 * The thread *view* (``/app/threads`` etc.) hosts a single right-panel that
 * shows whichever node is currently active, falling back to nothing. This is
 * different from the global Node panel that lives below Topbar 2 — the
 * thread view's main content already *is* the Thread, so the side panel
 * here is for inspecting referenced nodes.
 */
export function ThreadPanelLayout() {
  const activePanelNodeId = State((store) => store.activePanelNodeId);
  const isNodePanelOpen = State((store) => store.isNodePanelOpen);
  // ``activePanelNodeId`` persists across views via route state, so when the
  // user opens an execution log row or a workflow node panel in RulesView
  // and then switches to the thread view, the synthetic ``:panel`` sentinel
  // can leak into here. Skip the ``node(id)`` query for those — the backend
  // can't resolve them and the thread view has no panel UI for them anyway.
  const isSynthetic = isSyntheticPanelId(activePanelNodeId);
  const { data, loading: isLoading } = useQuery(
    Queries.NODE_QUERY_WITH_PENDING_CHANGE,
    {
      variables: activePanelNodeId
        ? {
            id: activePanelNodeId,
            startDate:
              State.getState().timeSeriesFilter.startDate?.toISOString(),
            endDate: State.getState().timeSeriesFilter.endDate?.toISOString(),
          }
        : undefined,
      skip: !activePanelNodeId || isSynthetic,
    },
  );

  const typename = idToTypename(activePanelNodeId);

  const { store } = useNodeSetup({
    mode: "update",
    node: data?.node as IObjectWithTypename,
    typename: typename,
  });
  const { actions, metadata } = useNode(store);

  if (activePanelNodeId != null) {
    actions.push({
      label: "Fullscreen",
      icon: MaximizeIcon,
      isIconButton: true,
      onClick: () => {
        State.getState().pushToCommandStack({
          commandDefinitionId: `update-${typename}`,
          context: {
            entity: data?.node as EntityFragment,
            nodeId: activePanelNodeId,
            entityId: metadata.entityId,
            entityIdentifier: idToIdentifier(metadata.entityId),
          } as UpdateNodeContextType,
        });
      },
    });
  }

  const showNode = isNodePanelOpen && activePanelNodeId != null;

  return (
    <StickyLayout
      stickyTopChildren={
        <NodeTitleLayout
          store={showNode ? store : null}
          title={showNode ? metadata.title : null}
          isLoading={isLoading}
          actions={showNode ? actions : undefined}
        />
      }
      stickyTopClassName="[border-bottom-width:1px] [border-bottom-style:solid] border-border"
    >
      {showNode && (
        <NodeLayout store={store} isLoading={isLoading} isSingleClick={true} />
      )}
    </StickyLayout>
  );
}
